import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import type { UserProfile } from '../types';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', { event, session: session?.user?.email });
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      console.log('fetchProfile called with userId:', userId);
      let { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.log('Profile fetch error:', error);
        // If profile doesn't exist, create it
        if (error.code === 'PGRST116') {
          console.log('Profile not found, creating new profile for user:', userId);
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert([
              {
                id: userId,
                role: 'user', // Default role
                email: user?.email || null
              }
            ])
            .select()
            .single();
          
          if (createError) {
            console.error('Error creating profile:', createError);
            throw createError;
          }
          data = newProfile;
        } else {
          throw error;
        }
      }
      
      console.log('Profile data fetched:', data);
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { data, error };
  };

  const signOut = async () => {
    console.log('signOut called');
    const { error } = await supabase.auth.signOut();
    console.log('signOut result:', { error });
    return { error };
  };

  const updateAIModel = async (aiModel: 'openai' | 'ollama') => {
    if (!user || !profile) {
      return { error: new Error('No user logged in') };
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ ai_model: aiModel, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (error) throw error;

      setProfile(prev => prev ? { ...prev, ai_model: aiModel } : null);
      return { error: null };
    } catch (error) {
      console.error('Error updating AI model:', error);
      return { error: error as Error };
    }
  };

  const isSuperuser = profile?.role === 'superuser';
  const isAuthority = profile?.role === 'authority';
  console.log('useAuth state:', {
    user: user?.email,
    profile: profile?.role,
    isSuperuser,
    isAuthority,
    loading
  });

  return {
    user,
    profile,
    loading,
    isSuperuser,
    isAuthority,
    signIn,
    signUp,
    signOut,
    updateAIModel,
  };
}